/** The site's small design system: eyebrow labels, two button shapes, the
 *  photo hero, section containers, flush images, cards and text blocks.
 *  Colours and type come from the tokens in styles/index.css; nothing in here
 *  carries a literal colour. */

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { mediaSrcSet, mediaUrl } from '../lib/api'

// ---------------------------------------------------------------------------
// Eyebrow: small brass label above a heading
// ---------------------------------------------------------------------------

export function Eyebrow({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <p className={`eyebrow m-0 ${className}`} data-testid="eyebrow">
      {children}
    </p>
  )
}

// ---------------------------------------------------------------------------
// Buttons: solid ink (primary) and a thin outline (secondary)
// ---------------------------------------------------------------------------

const buttonClass =
  'inline-flex cursor-pointer items-center justify-center gap-2 rounded-[2px] bg-ink px-[22px] py-[13px] ' +
  'font-sans text-[15px] font-semibold leading-none text-paper transition-colors duration-200 ' +
  'hover:bg-walnut focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber ' +
  'disabled:cursor-not-allowed disabled:opacity-60'

export function ButtonLink({
  to,
  children,
  className = '',
}: {
  to: string
  children: ReactNode
  className?: string
}) {
  return (
    <Link to={to} className={`${buttonClass} ${className}`} data-testid="primary-button">
      {children}
    </Link>
  )
}

export function Button({
  children,
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button className={`${buttonClass} ${className}`} data-testid="primary-button" {...props}>
      {children}
    </button>
  )
}

/** Outline button. `tone="light"` is for dark bands, where an ink border
 *  would vanish. */
export function OutlineButtonLink({
  to,
  children,
  className = '',
  tone = 'dark',
}: {
  to: string
  children: ReactNode
  className?: string
  tone?: 'dark' | 'light'
}) {
  const colours =
    tone === 'light'
      ? 'border-paper/70 text-paper hover:bg-paper/10'
      : 'border-ink text-ink hover:bg-paper-deep'
  return (
    <Link
      to={to}
      data-testid="outline-button"
      className={
        'inline-flex items-center justify-center rounded-[2px] border bg-transparent px-[22px] py-[12px] ' +
        'font-sans text-[15px] font-semibold leading-none transition-colors duration-200 ' +
        `focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber ${colours} ${className}`
      }
    >
      {children}
    </Link>
  )
}

/** Full-bleed photo with a dark wash and a left-aligned title, used on
 *  Über mich and Kontakt. */
export function PhotoHero({
  image,
  title,
  subtitle,
}: {
  image: string
  title: string
  subtitle?: string
}) {
  return (
    <section
      className="relative flex min-h-[clamp(360px,52vh,600px)] items-end overflow-hidden bg-night bg-cover bg-center"
      style={{ backgroundImage: `url(${image})` }}
      data-testid="photo-hero"
    >
      <div aria-hidden="true" className="absolute inset-0 bg-night/55" />
      <div className="relative z-[1] mx-auto w-full max-w-[1200px] px-6 pb-14 pt-24 md:px-10 md:pb-20">
        <h1
          className="m-0 max-w-[14ch] font-serif text-[clamp(40px,6vw,68px)] font-medium leading-[1.05] text-paper"
          data-testid="hero-title"
        >
          {title}
        </h1>
        {subtitle ? (
          <p
            className="m-0 mt-4 max-w-[40ch] font-serif text-[clamp(20px,2.4vw,26px)] italic leading-[1.35] text-paper/85"
            data-testid="hero-subtitle"
          >
            {subtitle}
          </p>
        ) : null}
      </div>
    </section>
  )
}

/** Compact button for the admin area and secondary actions. */
export function PlainButton({
  children,
  variant = 'primary',
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'ghost' }) {
  const base =
    'inline-flex cursor-pointer items-center justify-center rounded-[2px] px-5 py-2.5 font-sans text-[15px] font-semibold leading-none ' +
    'transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber ' +
    'disabled:cursor-not-allowed disabled:opacity-60'
  const tone =
    variant === 'primary'
      ? 'bg-ink text-paper hover:bg-walnut'
      : 'border border-line bg-transparent text-ink hover:bg-paper-deep'
  return (
    <button className={`${base} ${tone} ${className}`} {...props}>
      {children}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Section + Row: 1200px content column
// ---------------------------------------------------------------------------

export function Section({
  children,
  className = '',
  background,
  id,
  ...rest
}: {
  children: ReactNode
  className?: string
  background?: string
  id?: string
} & React.HTMLAttributes<HTMLElement>) {
  return (
    <section
      id={id}
      className={`px-6 py-16 md:px-10 md:py-24 ${className}`}
      style={background ? { backgroundColor: background } : undefined}
      {...rest}
    >
      {children}
    </section>
  )
}

export function Row({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`mx-auto w-full max-w-[1200px] ${className}`}>{children}</div>
}

// ---------------------------------------------------------------------------
// Image: flush, hairline border, no frame
// ---------------------------------------------------------------------------

export function FramedImage({
  filename,
  alt,
  className = '',
  sizes = '(max-width: 768px) 100vw, 480px',
  loading = 'lazy',
  aspect,
}: {
  filename: string
  alt: string
  className?: string
  sizes?: string
  loading?: 'lazy' | 'eager'
  /** Tailwind aspect class (e.g. "aspect-[3/4]"): crops every image to the
   *  same shape so a card grid lines up - used by the shop. */
  aspect?: string
}) {
  if (!filename) return null
  return (
    <span className={`block overflow-hidden rounded-[2px] border border-line bg-paper-deep ${className}`}>
      <img
        src={mediaUrl(filename)}
        srcSet={mediaSrcSet(filename)}
        sizes={sizes}
        alt={alt}
        loading={loading}
        decoding="async"
        className={aspect ? `block w-full object-cover ${aspect}` : 'block h-auto w-full'}
      />
    </span>
  )
}

// ---------------------------------------------------------------------------
// Card: image, serif title, optional description. Backs the homepage
// workshop tiles, every gallery tile, and the shop cards.
// ---------------------------------------------------------------------------

export function BlurbCard({
  filename,
  alt,
  title,
  description,
  to,
  onClick,
  footer,
  eager = false,
  imageAspect,
}: {
  filename: string
  alt: string
  title: string
  description?: string
  to?: string
  onClick?: () => void
  footer?: ReactNode
  eager?: boolean
  /** Uniform image crop for grids that must line up (shop). */
  imageAspect?: string
}) {
  const body = (
    <>
      {filename ? (
        <FramedImage
          filename={filename}
          alt={alt || title}
          loading={eager ? 'eager' : 'lazy'}
          className="mb-4 transition-opacity duration-200 group-hover:opacity-90"
          aspect={imageAspect}
        />
      ) : null}
      <h4 className="m-0 font-serif text-[26px] font-medium leading-[1.25] text-ink" data-testid="blurb-title">
        {title}
      </h4>
      {description ? (
        <p className="m-0 mt-1 font-sans text-[16px] leading-[1.5] text-slate">{description}</p>
      ) : null}
      {footer}
    </>
  )

  const shared = 'group block w-full text-left'

  if (to) {
    return (
      <Link to={to} className={shared} data-testid="blurb">
        {body}
      </Link>
    )
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${shared} cursor-pointer`} data-testid="blurb">
        {body}
      </button>
    )
  }
  return (
    <div className={shared} data-testid="blurb">
      {body}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Heading + text
// ---------------------------------------------------------------------------

export function SectionHeading({
  children,
  className = '',
  as: Tag = 'h2',
}: {
  children: ReactNode
  className?: string
  as?: 'h1' | 'h2' | 'h3'
}) {
  return (
    <Tag className={`m-0 font-serif text-[clamp(32px,4vw,44px)] font-medium leading-[1.15] text-ink ${className}`}>
      {children}
    </Tag>
  )
}

/** Renders a stored HTML block. Content comes from our own database (written
 *  by the site owner in the admin), never from visitor input. */
export function RichText({ html, className = '' }: { html: string; className?: string }) {
  return (
    <div
      className={`font-sans text-[18px] leading-[1.65] text-ink [&_p]:mb-[1em] [&_a]:underline [&_h1]:font-serif [&_h1]:text-[40px] [&_h2]:font-serif [&_h2]:text-[36px] [&_h3]:font-serif [&_h3]:text-[30px] [&_h4]:font-serif [&_h4]:text-[24px] [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 ${className}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

/** Plain-text block: one paragraph per line. */
export function TextBlock({ text, className = '' }: { text: string; className?: string }) {
  return (
    <div className={`font-sans text-[18px] leading-[1.65] text-ink ${className}`}>
      {text.split('\n').map((line, index) => (
        <p key={index} className="m-0 mb-[1em] last:mb-0">
          {line}
        </p>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Status pill for shop products
// ---------------------------------------------------------------------------

export function StatusBadge({ status, label }: { status: string; label: string }) {
  const tone =
    status === 'available'
      ? 'border-amber/50 bg-amber/15 text-ink'
      : status === 'reserved'
        ? 'border-line bg-paper-deep text-ink'
        : 'border-slate bg-slate text-paper'
  return (
    <span
      className={`inline-block rounded-full border px-3 py-[3px] font-sans text-[12px] font-semibold uppercase tracking-[0.08em] ${tone}`}
      data-testid="status-badge"
      data-status={status}
    >
      {label}
    </span>
  )
}
